<template>
  <div class="container">
    <LoadingBox v-if="loading" />
    <div v-if="!loading">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h4 class="title is-4">
              {{ report.applicationRef.organizationName }}
            </h4>
          </div>
        </div>
        <div class="level-right">
          <h4 class="title is-5 has-text-primary">
            {{ report.published ? 'Published' : 'Not Published' }}
          </h4>
        </div>
      </div>
      <div class="content" v-if="evaluations.length === 0">
        <em>There are no evaluations yet for this application.</em>
      </div>
      <div class="content" v-if="pendingCommitments.length">
        <h4>Pending Commitmments</h4>
        <div v-for="pc in pendingCommitments" :key="pc.commitment.id">
          <router-link
            :to="{
              name: 'EvaluatorDetail',
              params: { evaluatorId: pc.evaluator.id },
            }"
            target="_blank"
            >{{ pc.evaluator.fullName }}
          </router-link>
          <StaffCommitmentSummary :commitment="pc.commitment" />
        </div>
      </div>
      <draggable :list="validEvaluations" @end="updateEvaluationOrder">
        <div class="box drag-item" v-for="evaluation in validEvaluations" :key="evaluation.id">
          <div class="level">
            <div class="level-left">
              <div class="level-item">
                <div>
                  <strong>{{ evaluation.evaluator.fullName }}</strong
                  ><br />
                  {{ evaluation.evaluator.email }}<br />
                </div>
              </div>
            </div>
            <div class="level-right">
              <input
                type="checkbox"
                class="checkbox"
                v-model="evaluation.includeInReport"
                @input="toggleIncludeEvaluation(evaluation)"
              />
              <label style="marginLeft: 1rem; marginRight: 2rem;">Include in Report</label>
              <button class="button" @click="evaluation.isHidden = !evaluation.isHidden">
                {{ evaluation.isHidden ? 'Show Evaluation' : 'Hide Evaluation' }}
              </button>
            </div>
          </div>

          <draggable :list="evaluation.responses" @end="updateResponseOrder">
            <div v-if="!evaluation.isHidden">
              <div v-for="response in getEvaluationResponses(evaluation)" :key="response.id">
                <hr />
                <div class="content">
                  <div class="level">
                    <div class="level-left response-container">
                      <div class="response-container__header">
                        <strong>
                          {{
                            response.questionRef.questionText !== 'Comments'
                              ? response.questionRef.questionText
                              : response.questionRef.helpText
                          }}
                        </strong>
                        <div v-if="response.questionRef.questionType === 'OPEN_TEXT'">
                          <button
                            class="button is-primary"
                            style="marginLeft: 1rem;"
                            v-if="!response.beingEdited"
                            @click="response.beingEdited = true"
                          >
                            Edit
                          </button>
                          <button
                            class="button is-primary is-outlined"
                            style="marginLeft: 1rem;"
                            v-if="response.beingEdited"
                            @click="updateResponse(response)"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                      <ul v-if="!response.beingEdited">
                        <li class="break-pars">{{ response.response }}</li>
                      </ul>
                      <textarea
                        class="textarea"
                        style="width: 50rem; marginTop: 1rem;"
                        v-if="response.beingEdited"
                        v-model="response.response"
                      />
                    </div>
                    <div class="level-right">
                      <input
                        type="checkbox"
                        class="checkbox"
                        v-model="response.includeInReport"
                        @input="toggleIncludeResponse(response)"
                      />
                      <label style="marginLeft: 1rem; marginRight: 2rem;">Include in Report</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </draggable>
        </div>
      </draggable>
      <div class="content" v-if="commitments.length">
        <h4>Follow-Ups</h4>
        <div
          v-for="commitment in commitments"
          :key="commitment.id"
          style="display: flex; alignItems: center;"
        >
          <input
            type="checkbox"
            class="checkbox"
            v-model="commitment.includeInReport"
            @input="toggleIncludeCommitment(commitment)"
          />
          <p style="marginLeft: 1rem;">
            <router-link
              :to="{
                name: 'EvaluatorDetail',
                params: { evaluatorId: commitment.evaluator.id },
              }"
              target="_blank"
              >{{ commitment.evaluator.fullName }}
            </router-link>
            ({{ commitment.name }}): {{ commitment.description }}
          </p>
        </div>
      </div>
      <div class="content" v-if="files.length">
        <h4>Canned Files</h4>
        <div v-for="file in files" :key="file.id" style="display: flex; alignItems: center;">
          <input
            type="checkbox"
            class="checkbox"
            v-model="file.includeInReport"
            @input="toggleIncludeFile(file)"
          />
          <p style="marginLeft: 1rem;">{{ file.name }}</p>
        </div>
      </div>
      <div class="content">
        <h4>Preparer Comments</h4>
        <p style="font-style:italic;color:#777">
          Changes are saved when you click outside this box
        </p>
        <textarea
          rows="6"
          cols="50"
          v-model="report.universalFeedback"
          @blur="updatePreparerComment"
        >
        </textarea>
      </div>
      <div class="content">
        <div v-if="!report.published">
          <button
            class="button is-primary"
            style="width: 10rem; marginRight: 1rem;"
            @click="togglePublish"
          >
            Publish
          </button>
          <button class="button is-primary is-outlined" style="width: 10rem;" @click="preview">
            Preview
          </button>
        </div>
        <div v-if="report.published">
          <button
            class="button is-primary"
            style="width: 10rem; marginRight: 1rem;"
            @click="togglePublish"
          >
            Unpublish
          </button>
          <button class="button is-primary is-outlined" style="width: 10rem;" @click="preview">
            View
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Application from '@/services/applications'
import ApplicationReport from '@/services/applicationReports'
import Evaluation from '@/services/evaluations'
import EvaluationResponse from '@/services/evaluationResponses'
import Commitment from '@/services/commitments'
import draggable from 'vuedraggable'

import LoadingBox from '@/components/LoadingBox'
import StaffCommitmentSummary from '@/components/StaffCommitmentSummary'

export default {
  name: 'StaffReportGeneration',
  components: {
    LoadingBox,
    StaffCommitmentSummary,
    draggable,
  },
  filters: {
    toTitle(value) {
      let str = value.toLowerCase().split('_')
      for (var i = 0; i < str.length; i++) {
        str[i] = str[i].charAt(0).toUpperCase() + str[i].slice(1)
      }
      return str.join(' ')
    },
  },
  data() {
    return {
      loading: true,
      report: null,
      evaluations: [],
      validEvaluations: [],
      files: [],
    }
  },
  computed: {
    pendingCommitments() {
      const result = []
      this.evaluations.forEach(evaluation => {
        evaluation.commitments.forEach(comm => {
          if (comm.status === 'PENDING') {
            result.push({
              evaluator: evaluation.evaluator,
              commitment: comm,
            })
          }
        })
      })
      return result
    },
    commitments() {
      let commitments = []
      this.evaluations.forEach(evaluation => {
        const approvedCommitments = evaluation.commitments.filter(c => c.status === 'APPROVED')
        if (approvedCommitments.length) {
          commitments = commitments.concat(approvedCommitments)
        }
      })
      return commitments
    },
  },
  created() {
    const applicationId = this.$route.params.applicationId
    this.fetchData(applicationId)
  },
  methods: {
    async fetchData(applicationId) {
      // Fetch the report and evaluations for a specific application
      this.loading = true
      try {
        const report = await Application.api.getApplicationReport(applicationId)
        this.origComment = report.universalFeedback
        const evaluations = await Application.api.getEvaluations(applicationId)
        evaluations.forEach(evaluation => {
          evaluation.isHidden = true
          evaluation.responses.forEach(response => {
            response.beingEdited = false
          })
          // Tack the evaluator onto the commitments so that we can easily show evaluator names next to rendered commitments
          evaluation.commitments.forEach(commitment => {
            commitment.evaluator = evaluation.evaluator
          })
        })
        const files = await ApplicationReport.api.getReportFiles(report.applicationRef.cohort)
        files.forEach(file => {
          file.includeInReport = report.files.includes(file.id)
        })
        this.report = report
        this.evaluations = evaluations
        /** Sort Evaluations */
        const evalWithResponses = this.evaluations.filter(
          evaluation => this.getEvaluationResponses(evaluation).length)
        const evalWithOrder = evalWithResponses.filter(
          evaluation => (evaluation.applicationOrder != null && evaluation.applicationOrder > 0))
        const evalWithoutOrder = evalWithResponses.filter(evaluation => (evaluation.applicationOrder === null || evaluation.applicationOrder === 0))
        this.validEvaluations = evalWithOrder.sort(function(a, b) {
          return a.applicationOrder - b.applicationOrder
        }).concat(evalWithoutOrder)

        /** Sort Based on eval order and use default order if none */
        this.validEvaluations.forEach((evaluation) => {
          if (evaluation.responses[0].evaluationOrder != null) {
            evaluation.responses.sort(function(a, b) {
              return a.evaluationOrder - b.evaluationOrder
            })
          } else {
            const withoutSection = evaluation.responses.filter(response => response.questionRef.section === null)
            const withSection = evaluation.responses.filter(response => response.questionRef.section != null)

            withSection.sort(function(a, b) {
              const secA = evaluation.sections.filter(section => section.id === a.questionRef.section)
              const secB = evaluation.sections.filter(section => section.id === b.questionRef.section)
              if (secA.length > 0 && secB.length > 0) {
                var n =  secA[0].order - secB[0].order;
              } else {
                var n = 1
              }
              if (n !== 0) {
                return n;
              }
              return b.questionRef.order - a.questionRef.order;
            })
            evaluation.responses = withSection.concat(withoutSection)
            evaluation.responses.forEach((response, i) => {
              response.evaluationOrder = i + 1 
              EvaluationResponse.api.update(response, ['evaluationOrder'])
            })
          }
        })

        this.files = files
      } finally {
        this.loading = false
      }
    },
    getEvaluationResponses(evaluation) {
      return evaluation.responses.filter(
        response => response.questionRef.questionType !== 'SCALE' && response.response !== null,
      )
    },
    updateEvaluationOrder() {
      this.validEvaluations.forEach((evaluation, i) => (evaluation.applicationOrder = i + 1))
      this.validEvaluations.forEach(evaluation => {
        Evaluation.api.update(evaluation, ['applicationOrder'])
      })
    },
    updateResponseOrder(e) {
      const currentEval = this.validEvaluations.filter(
        evaluation => evaluation.id === e.item._underlying_vm_.evaluation,
      )[0]
      currentEval.responses.forEach((response, i) => {
        response.evaluationOrder = i + 1
        EvaluationResponse.api.update(response, ['evaluationOrder'])
      })
    },
    async toggleIncludeEvaluation(evaluation) {
      evaluation.includeInReport = !evaluation.includeInReport
      await Evaluation.api.update(evaluation, ['includeInReport'])
    },
    async updatePreparerComment() {
      const newComment = this.report.universalFeedback
      if (this.origComment != newComment) {
        await ApplicationReport.api.update(this.report, ['universalFeedback'])
        this.origComment = newComment
      }
    },
    async updateResponse(response) {
      response.beingEdited = false
      // NA Problem
      const newResponse = await EvaluationResponse.api.update(response, [
        'includeInReport',
        'response',
      ])
      return newResponse
    },
    async toggleIncludeResponse(response) {
      response.includeInReport = !response.includeInReport
      this.updateResponse(response)
    },
    async toggleIncludeCommitment(commitment) {
      commitment.includeInReport = !commitment.includeInReport
      await Commitment.api.update(commitment, ['includeInReport'])
    },
    async toggleIncludeFile(file) {
      // Update list of files on the ApplicationReport object

      file.includeInReport = !file.includeInReport
      const report = this.report.copy()
      let updatedFiles = []
      if (file.includeInReport) {
        updatedFiles = [file.id, ...report.files]
      } else {
        updatedFiles = report.files.filter(f => f !== file.id)
      }

      report.files = updatedFiles
      this.report.files = report.files
      await ApplicationReport.api.update(report, ['files'])
    },
    preview() {
      const url = `/application-reports/${this.report.application}/`
      window.open(url, '_blank')
    },
    async togglePublish() {
      // Toggle the published status of the report
      const report = this.report.copy()
      report.published = !report.published

      await ApplicationReport.api.update(report, ['published'])
      this.report = report
    },
  },
}
</script>

<style lang="scss" scoped>
.checkbox {
  cursor: pointer;
  display: inline-block;
  line-height: 1.25;
  position: relative;
  width: 20px;
  height: 20px;
}
.response-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 60rem;

  &__header {
    display: flex;
    align-items: flex-start;
  }
}

.drag-item {
  cursor: move;
}
.break-pars {
  white-space: pre-wrap;
}
</style>
